// ============================================================
//  Template warnings — derived data-quality signals.
//
//  Single source of truth for "this template needs human review".
//  Used by:
//    - Validators in /documents/calculator-knowledge/* (CI/dev visibility)
//    - CategoryTemplatesView (admin/contractor-visible chip on the card)
//
//  Computing warnings from the data (not storing them) means a fix to the
//  underlying numbers in a future revision automatically clears the flag.
//
//  Convention (locked in §findings of the v2 review):
//    unitPriceUsd is COST. defaultTotalUsdMedian is RETAIL. The gap is
//    contractor margin captured by workspace.targetMarginPercent.
//
//  Therefore a healthy template has:
//    line_sum × (1 + reasonable_margin)  ≈  median
//  i.e. line_sum < median, by an amount that maps to a 0%–60% margin band.
//
//  When line_sum >= median, the data is wrong — there is no margin path
//  that reconciles them. That's the data-review case (TRAQ).
// ============================================================

import type { EstimateTemplate } from './estimateTemplateTypes';

export type WarningSeverity = 'info' | 'warn' | 'error';

export interface TemplateWarning {
  ruleId: string;
  severity: WarningSeverity;
  message: string;
}

/** Plausible upper bound for "the gap between cost and retail is just
 *  margin". A 70% margin is already premium — anything higher and the
 *  difference is more likely a data error than a real margin. */
const HIGH_MARGIN_THRESHOLD_PCT = 70;

/** Compute every warning that applies to a template. Empty array means clean. */
export function getTemplateWarnings(template: EstimateTemplate): TemplateWarning[] {
  const out: TemplateWarning[] = [];

  const lineSum = (template.lineItems ?? []).reduce(
    (s, li) => s + (li.qty || 0) * (li.unitPriceUsd || 0),
    0,
  );
  const median = template.defaultTotalUsdMedian;

  // 1. line_sum >= median → data is internally inconsistent (no margin reconciles)
  if (median > 0 && lineSum > median * 1.001) {
    out.push({
      ruleId: 'line-sum-exceeds-median',
      severity: 'error',
      message: `Line items at cost ($${lineSum.toFixed(2)}) exceed retail median ($${median.toLocaleString()}). Either the line item rates/qty are too high, or the median is too low. Cannot reconcile via margin.`,
    });
  }

  // 2. Implied margin too high → median may be too high or line items too low
  if (median > 0 && lineSum > 0 && lineSum < median) {
    const impliedMarginPct = ((median - lineSum) / median) * 100;
    if (impliedMarginPct > HIGH_MARGIN_THRESHOLD_PCT) {
      out.push({
        ruleId: 'implied-margin-very-high',
        severity: 'warn',
        message: `Implied margin from cost-basis line items to retail median is ${impliedMarginPct.toFixed(0)}%. That's higher than typical. Verify median price or line-item costs.`,
      });
    }
  }

  // 3. Confidence advisory
  if (template.confidence === 'estimated') {
    out.push({
      ruleId: 'confidence-estimated',
      severity: 'info',
      message: 'Pricing is estimated, not sourced or triangulated. Treat as a starting point and adjust per-tenant.',
    });
  }

  // 4. hasCalculatorVariant points to a calculatorTemplateId, but the bundle
  //    presence is checked in the UI layer (it has access to the loader).
  //    No warning here — leaving for the view layer.

  return out;
}

/** True if the template has any error-severity warnings. UI uses this to pick
 *  chip color (amber for warn, red for error, etc.). */
export function hasErrors(warnings: TemplateWarning[]): boolean {
  return warnings.some(w => w.severity === 'error');
}

export function highestSeverity(warnings: TemplateWarning[]): WarningSeverity | null {
  if (warnings.some(w => w.severity === 'error')) return 'error';
  if (warnings.some(w => w.severity === 'warn')) return 'warn';
  if (warnings.some(w => w.severity === 'info')) return 'info';
  return null;
}
